import { useState, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { ToolCallDisplay } from '../../../contracts/tool-call-display';
import { getComputerUseTaskDisplay } from '../../../data/projects/components/ComputerUseTaskBlock.task-display';
import { agentTodoMessages } from '../../../data/projects/timeline/agent-todo/agentTodo.messages';
import { InlineApprovalActions } from '../../inline-approval/inline-approval-actions';
import { ComputerUsePlanCard } from './ComputerUsePlanCard';

/**
 * Renders why Approve is held (the desktop is busy). The host supplies the RPA
 * root's desktop-control warning; `className` carries the surrounding spacing.
 */
type RenderBlockedWarning = (message: string, className?: string) => ReactNode;

export const AwaitingApprovalCard = ({
  task,
  planSteps,
  toolCallDisplay,
  recordedWorkflow,
  onApprove,
  onDeny,
  approveBlockedMessage,
  renderBlockedWarning,
}: {
  task?: string;
  planSteps?: readonly string[];
  toolCallDisplay?: ToolCallDisplay;
  /**
   * The recorded routine's plan, rendered by the host: it is the RPA recorder's
   * presentation, so it crosses as a slot.
   */
  recordedWorkflow?: ReactNode;
  onApprove?: () => void | Promise<void>;
  onDeny?: () => void | Promise<void>;
  approveBlockedMessage?: string;
  renderBlockedWarning: RenderBlockedWarning;
}) => {
  const intl = useIntl();
  const approveDisabled = !!approveBlockedMessage;
  const { pendingVerdict, handleApprove, handleDeny } = useAwaitingApprovalVerdict(
    onApprove,
    onDeny,
    approveDisabled
  );
  const { goal, planSteps: steps } = getComputerUseTaskDisplay(
    task,
    intl,
    toolCallDisplay,
    planSteps
  );

  return (
    <ComputerUsePlanCard
      title={goal}
      steps={steps}
      phase="awaiting"
      testId="cu-awaiting-approval-card"
      footer={
        <InlineApprovalActions
          onApprove={handleApprove}
          onDeny={handleDeny}
          pendingVerdict={pendingVerdict}
          disabled={!onApprove && !onDeny}
          approveDisabled={approveDisabled}
          approveLabel={intl.formatMessage(agentTodoMessages.startPlan)}
          denyLabel={intl.formatMessage(agentTodoMessages.declinePlan)}
        />
      }
    >
      {recordedWorkflow}
      {approveBlockedMessage && renderBlockedWarning(approveBlockedMessage)}
    </ComputerUsePlanCard>
  );
};

export const AwaitingApprovalSection = ({
  onApprove,
  onDeny,
  approveBlockedMessage,
  renderBlockedWarning,
}: {
  onApprove?: () => void | Promise<void>;
  onDeny?: () => void | Promise<void>;
  approveBlockedMessage?: string;
  renderBlockedWarning: RenderBlockedWarning;
}) => {
  const approveDisabled = !!approveBlockedMessage;
  const { pendingVerdict, handleApprove, handleDeny } = useAwaitingApprovalVerdict(
    onApprove,
    onDeny,
    approveDisabled
  );
  return (
    <div
      className="border-t border-border/60 pt-3"
      data-testid="cu-awaiting-approval-section"
    >
      {approveBlockedMessage && renderBlockedWarning(approveBlockedMessage, 'mb-2')}
      <InlineApprovalActions
        onApprove={handleApprove}
        onDeny={handleDeny}
        pendingVerdict={pendingVerdict}
        disabled={!onApprove && !onDeny}
        approveDisabled={approveDisabled}
      />
    </div>
  );
};

const useAwaitingApprovalVerdict = (
  onApprove?: () => void | Promise<void>,
  onDeny?: () => void | Promise<void>,
  approveDisabled = false
) => {
  const [pendingVerdict, setPendingVerdict] = useState<'approve' | 'deny' | null>(null);
  const handleApprove = async () => {
    if (pendingVerdict !== null || !onApprove || approveDisabled) return;
    setPendingVerdict('approve');
    try {
      await onApprove();
    } catch {
      setPendingVerdict(null);
    }
  };
  const handleDeny = async () => {
    if (pendingVerdict !== null || !onDeny) return;
    setPendingVerdict('deny');
    try {
      await onDeny();
    } catch {
      setPendingVerdict(null);
    }
  };
  return { pendingVerdict, handleApprove, handleDeny };
};
